import { useState, type CSSProperties } from "react";
import type { QuickAction } from "../overviewTypes";

/**
 * Sage "Create new" panel of 9 Quick Action circles.
 *
 * Responsive:
 *   desktop (>960px): repeat(6, 1fr) grid, 64px circles
 *   tablet (641-960px): repeat(4, 1fr) grid
 *   phone (<=640px): repeat(3, 1fr) grid, 54px circles
 */

interface QuickActionsRowProps {
  actions: QuickAction[];
  onActionClick: (action: QuickAction) => void;
}

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  accent: "#15A08C",
  sage: "#EDF2EE",
};

export function QuickActionsRow({ actions, onActionClick }: QuickActionsRowProps) {
  const DISPLAY_IDS = ["transaction", "invoice", "bill", "receipt", "transfer", "payment", "reconcile", "journal", "chart"];
  const displayActions = DISPLAY_IDS
    .map((id) => actions.find((a) => a.id === id))
    .filter((a): a is QuickAction => a !== undefined);

  if (displayActions.length === 0) return null;

  return (
    <section style={sectionStyle}>
      <p style={sectionLabelStyle}>Create new</p>
      <div style={panelStyle}>
        <div className="novala-qa-grid" style={gridStyle}>
          {displayActions.map((action) => (
            <ActionCircle key={action.id} action={action} onClick={() => onActionClick(action)} />
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 640px) {
          .novala-qa-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .novala-qa-circle { width: 54px !important; height: 54px !important; }
          .novala-qa-circle svg { width: 20px !important; height: 20px !important; }
          .novala-qa-tip { display: none !important; }
        }
        @media (min-width: 641px) and (max-width: 960px) {
          .novala-qa-grid { grid-template-columns: repeat(4, 1fr) !important; }
        }
      `}</style>
    </section>
  );
}

interface ActionCircleProps {
  action: QuickAction;
  onClick: () => void;
}

function ActionCircle({ action, onClick }: ActionCircleProps) {
  const [hover, setHover] = useState(false);
  const Icon = action.icon;

  return (
    <button
      type="button"
      style={itemStyle}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span className="novala-qa-circle" style={{
        ...circleStyle,
        transform: hover ? "scale(1.06)" : "scale(1)",
        boxShadow: hover
          ? "0 3px 8px rgba(14, 26, 26, 0.12)"
          : "0 1px 2px rgba(14, 26, 26, 0.06)",
      }}>
        <Icon size={24} strokeWidth={1.8} color={PALETTE.accent} />
        <span style={plusStyle}>+</span>
      </span>
      <span style={nameStyle}>{action.title}</span>
      {hover && action.tooltip && <span className="novala-qa-tip" style={tipStyle}>{action.tooltip}</span>}
    </button>
  );
}

const sectionStyle: CSSProperties = { marginBottom: 32 };

const sectionLabelStyle: CSSProperties = {
  fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em",
  textTransform: "uppercase", color: PALETTE.ink3, margin: "0 0 14px",
};

const panelStyle: CSSProperties = {
  background: PALETTE.sage,
  borderRadius: 10,
  padding: "14px 18px 20px",
  margin: "0 16px 0 4px",
};

const gridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(6, 1fr)",
  gap: "14px 6px",
};

const itemStyle: CSSProperties = {
  display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
  cursor: "pointer", padding: "4px 2px", borderRadius: 8,
  background: "transparent", border: 0, fontFamily: "inherit",
  position: "relative",
};

const circleStyle: CSSProperties = {
  width: 64, height: 64, borderRadius: "50%",
  background: "#fff",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  position: "relative",
  transition: "transform 150ms ease, box-shadow 150ms ease",
};

const plusStyle: CSSProperties = {
  position: "absolute", bottom: -2, right: -2,
  width: 20, height: 20, borderRadius: "50%",
  background: PALETTE.ink, color: "#fff",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontSize: 13, fontWeight: 700, lineHeight: 1,
  border: `2.5px solid ${PALETTE.sage}`,
};

const nameStyle: CSSProperties = {
  fontSize: 12, color: PALETTE.ink2, fontWeight: 500, textAlign: "center",
};

const tipStyle: CSSProperties = {
  position: "absolute", bottom: "calc(100% + 8px)", left: "50%",
  transform: "translateX(-50%)",
  background: PALETTE.ink, color: "#fff",
  fontSize: 11, fontWeight: 500, whiteSpace: "nowrap",
  padding: "5px 10px", borderRadius: 6,
  zIndex: 10, pointerEvents: "none",
};